import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  brand: "bb",
  nombre: "",
  tipo: "multifamily",
  contacto: { nombre: "", email: "", telefono: "" },
  condiciones_pago_dias: 30,
  notas: "",
  billing_brand: "",
  email_estimados: "",
  email_facturas: "",
  email_cobros: "",
  plataforma_facturacion: "ninguna",
  formato_cobro: "facturas_adjuntas",
  notas_cobranza: "",
};

const Field = ({ label, children }) => (
  <div>
    <Label className="mb-1.5 block text-[12px] text-ink-tertiary">{label}</Label>
    {children}
  </div>
);

export const CompanyDialog = ({ open, onOpenChange, company }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) {
      setForm(
        company
          ? {
              brand: company.brand,
              nombre: company.nombre,
              tipo: company.tipo,
              contacto: company.contacto || { nombre: "", email: "", telefono: "" },
              condiciones_pago_dias: company.condiciones_pago_dias ?? 30,
              notas: company.notas || "",
              billing_brand: company.billing_brand || "",
              email_estimados: company.email_estimados || "",
              email_facturas: company.email_facturas || "",
              email_cobros: company.email_cobros || "",
              plataforma_facturacion: company.plataforma_facturacion || "ninguna",
              formato_cobro: company.formato_cobro || "facturas_adjuntas",
              notas_cobranza: company.notas_cobranza || "",
            }
          : EMPTY
      );
    }
  }, [open, company]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setContacto = (key, value) =>
    setForm((f) => ({ ...f, contacto: { ...f.contacto, [key]: value } }));

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        condiciones_pago_dias: Number(form.condiciones_pago_dias),
        billing_brand: form.billing_brand || null,
      };
      if (company) return (await api.patch(`/companies/${company.id}`, payload)).data;
      return (await api.post("/companies", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["company"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      toast.success(company ? t("app.saved") : t("app.created"));
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="company-dialog" className="max-h-[90vh] max-w-[640px] overflow-y-auto rounded-[8px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {company ? t("companies.edit") : t("companies.new")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3.5">
          <div className="col-span-2">
            <Field label={t("companies.name")}>
              <Input
                data-testid="company-name-input"
                value={form.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                className="h-9 rounded-[6px] text-[13.5px]"
              />
            </Field>
          </div>
          <Field label={t("app.brand")}>
            <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
              <SelectTrigger data-testid="company-brand-select" className="h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bb">B&amp;B</SelectItem>
                <SelectItem value="betos">Betos</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("companies.type")}>
            <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="company-type-select" className="h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multifamily">{t("companies.types.multifamily")}</SelectItem>
                <SelectItem value="residencial">{t("companies.types.residencial")}</SelectItem>
                <SelectItem value="comercial">{t("companies.types.comercial")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="col-span-2 border-t border-line pt-3">
            <p className="mb-2.5 text-[12px] font-medium uppercase tracking-[0.05em] text-ink-tertiary">
              {t("companies.main_contact")}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Field label={t("app.name")}>
                <Input
                  data-testid="company-contact-name-input"
                  value={form.contacto.nombre || ""}
                  onChange={(e) => setContacto("nombre", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
              <Field label={t("app.email")}>
                <Input
                  data-testid="company-contact-email-input"
                  value={form.contacto.email || ""}
                  onChange={(e) => setContacto("email", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
              <Field label={t("app.phone")}>
                <Input
                  data-testid="company-contact-phone-input"
                  value={form.contacto.telefono || ""}
                  onChange={(e) => setContacto("telefono", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
            </div>
          </div>

          <Field label={t("companies.payment_terms")}>
            <Input
              data-testid="company-terms-input"
              type="number"
              value={form.condiciones_pago_dias}
              onChange={(e) => set("condiciones_pago_dias", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("companies.billing_brand")}>
            <Select value={form.billing_brand || "none"} onValueChange={(v) => set("billing_brand", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="company-billing-brand-select" className="h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("companies.billing_brand_none")}</SelectItem>
                <SelectItem value="bb">B&amp;B</SelectItem>
                <SelectItem value="betos">Betos</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label={t("app.notes")}>
              <Textarea
                data-testid="company-notes-input"
                value={form.notas || ""}
                onChange={(e) => set("notas", e.target.value)}
                className="min-h-[70px] rounded-[6px] text-[13.5px]"
              />
            </Field>
          </div>

          <div className="col-span-2 border-t border-line pt-3">
            <p className="mb-2.5 text-[12px] font-medium uppercase tracking-[0.05em] text-ink-tertiary">
              {t("companies.billing_section")}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Field label={t("companies.email_estimados")}>
                <Input
                  data-testid="company-email-estimados-input"
                  value={form.email_estimados || ""}
                  onChange={(e) => set("email_estimados", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
              <Field label={t("companies.email_facturas")}>
                <Input
                  data-testid="company-email-facturas-input"
                  value={form.email_facturas || ""}
                  onChange={(e) => set("email_facturas", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
              <Field label={t("companies.email_cobros")}>
                <Input
                  data-testid="company-email-cobros-input"
                  value={form.email_cobros || ""}
                  onChange={(e) => set("email_cobros", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label={t("companies.billing_platform")}>
                <Select value={form.plataforma_facturacion || "ninguna"} onValueChange={(v) => set("plataforma_facturacion", v)}>
                  <SelectTrigger data-testid="company-billing-platform-select" className="h-9 rounded-[6px] text-[13.5px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["ninguna", "ops", "vendor_cafe"].map((p) => (
                      <SelectItem key={p} value={p}>{t(`companies.billing_platforms.${p}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("companies.billing_format")}>
                <Select value={form.formato_cobro || "facturas_adjuntas"} onValueChange={(v) => set("formato_cobro", v)}>
                  <SelectTrigger data-testid="company-billing-format-select" className="h-9 rounded-[6px] text-[13.5px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["facturas_adjuntas", "estado_de_cuenta", "ambos"].map((f) => (
                      <SelectItem key={f} value={f}>{t(`companies.billing_formats.${f}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-3">
              <Field label={t("companies.billing_notes")}>
                <Textarea
                  data-testid="company-billing-notes-input"
                  value={form.notas_cobranza || ""}
                  onChange={(e) => set("notas_cobranza", e.target.value)}
                  className="min-h-[60px] rounded-[6px] text-[13.5px]"
                />
              </Field>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 rounded-[6px] text-[13px]"
            onClick={() => onOpenChange(false)}
            data-testid="company-cancel-button"
          >
            {t("app.cancel")}
          </Button>
          <Button
            data-testid="company-save-button"
            disabled={!form.nombre.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
          >
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
